import React, { useState, useEffect } from 'react';
import {
  Mail,
  Send,
  Settings,
  AlertCircle,
  CheckCircle,
  Lock,
  Server,
  User,
  Save,
  TestTube
} from 'lucide-react';
import {
  getEmailConfig,
  updateEmailConfig,
  testEmailConnection
} from '../../../api/wholesale/invoices';
import LoadingSpinner from '../../../components/common/LoadingSpinner';

const EmailSettingsPage = () => {
  const [config, setConfig] = useState({
    enabled: false,
    smtp_host: '',
    smtp_port: 587,
    smtp_secure: true,
    smtp_user: '',
    smtp_password: '',
    from_email: '',
    from_name: ''
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    fetchEmailConfig();
  }, []);

  const fetchEmailConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getEmailConfig();
      if (response.success && response.data) {
        setConfig(response.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to load email configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setConfig((prev) => ({
      ...prev,
      [field]: value
    }));
    setSuccess(null);
    setError(null);
  };

  const handleToggleEnabled = () => {
    setConfig((prev) => ({
      ...prev,
      enabled: !prev.enabled
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await updateEmailConfig(config);
      if (response.success) {
        setSuccess('Email configuration saved successfully');
      }
    } catch (err) {
      setError(err.message || 'Failed to save email configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    setError(null);

    try {
      const response = await testEmailConnection();
      if (response.success) {
        setTestResult({
          success: true,
          message: 'Email service is connected and ready to send emails'
        });
      } else {
        setTestResult({
          success: false,
          message: response.error || 'Email service connection failed'
        });
      }
    } catch (err) {
      setTestResult({
        success: false,
        message: err.message || 'Failed to test email connection'
      });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-100 rounded-lg">
            <Mail className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Email Settings</h1>
            <p className="text-sm text-gray-600">
              Configure SMTP settings for sending invoices and reminders
            </p>
          </div>
        </div>
      </div>

      {/* Alert Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium text-red-900">Error</p>
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium text-green-900">Success</p>
            <p className="text-sm text-green-700">{success}</p>
          </div>
        </div>
      )}

      {testResult && (
        <div
          className={`border rounded-lg p-4 flex items-start gap-3 ${
            testResult.success ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'
          }`}
        >
          {testResult.success ? (
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          )}
          <div className="flex-1">
            <p
              className={`font-medium ${testResult.success ? 'text-green-900' : 'text-yellow-900'}`}
            >
              {testResult.success ? 'Connection Successful' : 'Connection Failed'}
            </p>
            <p className={`text-sm ${testResult.success ? 'text-green-700' : 'text-yellow-700'}`}>
              {testResult.message}
            </p>
          </div>
        </div>
      )}

      {/* Enable/Disable Toggle */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Send className="w-5 h-5 text-gray-600" />
            <div>
              <h3 className="font-semibold text-gray-900">Email Service</h3>
              <p className="text-sm text-gray-600">Enable or disable email notifications</p>
            </div>
          </div>
          <button
            onClick={handleToggleEnabled}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              config.enabled ? 'bg-emerald-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                config.enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {config.enabled && (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              Email service is enabled. Invoices and reminders will be sent automatically.
            </p>
          </div>
        )}
      </div>

      {/* SMTP Configuration */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Settings className="w-5 h-5 text-gray-600" />
            <h3 className="font-semibold text-gray-900">SMTP Configuration</h3>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Configure your SMTP server settings for sending emails
          </p>
        </div>

        <div className="p-6 space-y-6">
          {/* SMTP Host */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <div className="flex items-center gap-2">
                  <Server className="w-4 h-4" />
                  SMTP Host
                </div>
              </label>
              <input
                type="text"
                value={config.smtp_host}
                onChange={(e) => handleInputChange('smtp_host', e.target.value)}
                placeholder="smtp.gmail.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Your SMTP server address (e.g., smtp.gmail.com, smtp.office365.com)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Port</label>
              <input
                type="number"
                value={config.smtp_port}
                onChange={(e) => handleInputChange('smtp_port', parseInt(e.target.value))}
                placeholder="587"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Common ports: 587 (TLS), 465 (SSL), 25 (non-secure)
              </p>
            </div>
          </div>

          {/* Security */}
          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={config.smtp_secure}
                onChange={(e) => handleInputChange('smtp_secure', e.target.checked)}
                className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
              />
              <span className="text-sm font-medium text-gray-700">
                Use secure connection (TLS/SSL)
              </span>
            </label>
            <p className="text-xs text-gray-500 mt-1 ml-6">
              Recommended for security. Use TLS for port 587, SSL for port 465.
            </p>
          </div>

          {/* SMTP Username */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                SMTP Username
              </div>
            </label>
            <input
              type="text"
              value={config.smtp_user}
              onChange={(e) => handleInputChange('smtp_user', e.target.value)}
              placeholder="your-email@example.com"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
            <p className="text-xs text-gray-500 mt-1">Usually your email address</p>
          </div>

          {/* SMTP Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4" />
                SMTP Password
              </div>
            </label>
            <input
              type="password"
              value={config.smtp_password}
              onChange={(e) => handleInputChange('smtp_password', e.target.value)}
              placeholder="••••••••"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Your email password or app-specific password
            </p>
          </div>

          {/* From Email */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">From Email</label>
              <input
                type="email"
                value={config.from_email}
                onChange={(e) => handleInputChange('from_email', e.target.value)}
                placeholder="noreply@yourdomain.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                The email address that will appear as sender
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">From Name</label>
              <input
                type="text"
                value={config.from_name}
                onChange={(e) => handleInputChange('from_name', e.target.value)}
                placeholder="Eldaved BMS"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
              <p className="text-xs text-gray-500 mt-1">The name that will appear as sender</p>
            </div>
          </div>
        </div>
      </div>

      {/* Common SMTP Providers Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h4 className="font-semibold text-blue-900 mb-3">Common SMTP Settings</h4>
        <div className="space-y-2 text-sm text-blue-800">
          <p>
            <strong>Gmail:</strong> smtp.gmail.com, Port 587, TLS (Use App Password if 2FA enabled)
          </p>
          <p>
            <strong>Outlook/Office365:</strong> smtp.office365.com, Port 587, TLS
          </p>
          <p>
            <strong>Yahoo:</strong> smtp.mail.yahoo.com, Port 587, TLS
          </p>
          <p>
            <strong>SendGrid:</strong> smtp.sendgrid.net, Port 587, TLS (Use API key as password)
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-4">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Save className="w-5 h-5" />
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>

        <button
          onClick={handleTestConnection}
          disabled={testing || !config.enabled}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <TestTube className="w-5 h-5" />
          {testing ? 'Testing...' : 'Test Connection'}
        </button>
      </div>

      {/* Security Note */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Lock className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-yellow-900">Security Note</p>
            <p className="text-sm text-yellow-700 mt-1">
              Your SMTP password is stored securely. We recommend using app-specific passwords
              instead of your main email password, especially if you have 2-factor authentication
              enabled.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailSettingsPage;
